import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Headset, Loader2, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { createXRStore } from "@react-three/xr";
import { Toaster, toast } from "../components/ui/sonner";
import { Scene } from "../three/Scene";
import { Header } from "../components/Header";
import { Leaderboard, RANK_FILTERS } from "../components/Leaderboard";
import { ResiliencyDashboard } from "../components/ResiliencyDashboard";
import { ControlBar } from "../components/ControlBar";
import { ComparePanel } from "../components/ComparePanel";
import { HeatmapLegend } from "../components/HeatmapLegend";
import { TourCard } from "../components/TourCard";
import { ViewControls } from "../components/ViewControls";
import { SealManager } from "../components/SealManager";
import { RegionControls, REGIONS } from "../components/RegionControls";
import { categoryRanges, HEAT_CATEGORIES } from "../lib/scoring";
import { narrationText } from "../lib/narration";
import { stateName } from "../lib/states";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const xrStore = createXRStore();
const TOUR_SIZE = 5;
const TOUR_STEP_MS = 6000;

export default function ResiliencyMap() {
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [focusRank, setFocusRank] = useState(null);
  const [revealNonce, setRevealNonce] = useState(0);
  const [xrSupported, setXrSupported] = useState(false);

  const [category, setCategory] = useState("composite");
  const [bloom, setBloom] = useState(true);
  const [compareMode, setCompareMode] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [tourActive, setTourActive] = useState(false);
  const [tourIndex, setTourIndex] = useState(0);
  const [tourMuted, setTourMuted] = useState(false);
  const [embed, setEmbed] = useState(false);
  const [region, setRegion] = useState(null);
  const [showSeals, setShowSeals] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [leaderboardCollapsed, setLeaderboardCollapsed] = useState(false);
  const [rankFilter, setRankFilter] = useState("all");
  const [conference, setConference] = useState("all");
  const [instType, setInstType] = useState("all");
  const [sortKey, setSortKey] = useState("rank");
  const [hoveredCluster, setHoveredCluster] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [showHeader, setShowHeader] = useState(true);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showFooter, setShowFooter] = useState(true);

  const didInitFromUrl = useRef(false);

  useEffect(() => {
    axios
      .get(`${API}/institutions`)
      .then((r) => {
        const data = r.data;
        setInstitutions(data);
        if (didInitFromUrl.current) return;
        didInitFromUrl.current = true;
        const m = {};
        data.forEach((i) => (m[i.rank] = i));
        const sp = new URLSearchParams(window.location.search);
        if (sp.get("embed") === "1") {
          setEmbed(true);
          // Slim embed chrome: hide logo header + region footer, start panels
          // collapsed, and keep the state-bubble cluster view (no auto fly-in).
          setShowHeader(false);
          setShowFooter(false);
          setPanelCollapsed(true);
          setLeaderboardCollapsed(true);
        }
        const cat = sp.get("cat");
        if (cat && HEAT_CATEGORIES.some((c) => c.key === cat)) setCategory(cat);
        if (sp.get("bloom") === "0") setBloom(false);
        const cmp = sp.get("compare");
        const ranks = cmp
          ? cmp.split(",").map(Number).filter((rk) => m[rk]).slice(0, 2)
          : [];
        if (ranks.length) {
          setCompareMode(true);
          setCompareList(ranks);
        } else {
          const inst = sp.get("inst");
          if (inst && m[Number(inst)]) {
            setSelected(m[Number(inst)]);
            setFocusRank(Number(inst));
          } else {
            setSelected(data[0] || null);
          }
        }
      })
      .catch((e) => console.error("Failed to load institutions", e))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (navigator.xr?.isSessionSupported) {
      navigator.xr
        .isSessionSupported("immersive-vr")
        .then(setXrSupported)
        .catch(() => setXrSupported(false));
    }
  }, []);

  const ranges = useMemo(
    () => (institutions.length ? categoryRanges(institutions) : {}),
    [institutions]
  );

  const byRank = useMemo(() => {
    const m = {};
    institutions.forEach((i) => (m[i.rank] = i));
    return m;
  }, [institutions]);

  const rankMatch = (inst) => {
    switch (rankFilter) {
      case "top5":
        return inst.rank <= 5;
      case "top10":
        return inst.rank <= 10;
      case "top20":
        return inst.rank <= 20;
      case "top25":
        return inst.rank <= 25;
      case "top50":
        return inst.rank <= 50;
      case "r90":
        return inst.resiliency_rating >= 90;
      case "r85":
        return inst.resiliency_rating >= 85;
      default:
        return true;
    }
  };

  const conferences = useMemo(
    () =>
      Array.from(new Set(institutions.map((i) => i.conference).filter(Boolean))).sort(),
    [institutions]
  );

  const filteredInstitutions = useMemo(
    () =>
      institutions.filter(
        (i) =>
          rankMatch(i) &&
          (conference === "all" || i.conference === conference) &&
          (instType === "all" || i.type === instType)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [institutions, rankFilter, conference, instType]
  );

  const visibleRanks = useMemo(
    () => new Set(filteredInstitutions.map((i) => i.rank)),
    [filteredInstitutions]
  );

  // ---- Deep link: keep URL in sync with current view ----
  useEffect(() => {
    if (!didInitFromUrl.current) return;
    const params = new URLSearchParams();
    if (embed) params.set("embed", "1");
    if (compareMode && compareList.length) {
      params.set("compare", compareList.join(","));
    } else if (selected) {
      params.set("inst", String(selected.rank));
    }
    if (category !== "composite") params.set("cat", category);
    if (!bloom) params.set("bloom", "0");
    const qs = params.toString();
    const url = `${window.location.pathname}${qs ? "?" + qs : ""}`;
    window.history.replaceState(null, "", url);
  }, [selected, compareMode, compareList, category, bloom, embed]);

  // ---- Guided tour: auto-fly through the top 5 ----
  useEffect(() => {
    if (!tourActive) return;
    const top = institutions.slice(0, TOUR_SIZE);
    if (tourIndex >= top.length) {
      setTourActive(false);
      return;
    }
    const inst = top[tourIndex];
    setSelected(inst);
    setFocusRank(inst.rank);
    setRevealNonce((n) => n + 1);
    const t = setTimeout(() => setTourIndex((i) => i + 1), TOUR_STEP_MS);
    return () => clearTimeout(t);
  }, [tourActive, tourIndex, institutions]);

  // ---- Tour narration voiceover (Web Speech API) ----
  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    if (!tourActive || tourMuted) {
      window.speechSynthesis.cancel();
      return;
    }
    const inst = institutions[tourIndex];
    if (!inst) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(narrationText(inst));
    u.rate = 1;
    u.pitch = 1;
    window.speechSynthesis.speak(u);
  }, [tourActive, tourIndex, tourMuted, institutions]);

  useEffect(() => {
    return () => {
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    };
  }, []);

  const handleSelect = (inst) => {
    if (compareMode) {
      setCompareList((prev) => {
        if (prev.includes(inst.rank)) return prev.filter((r) => r !== inst.rank);
        if (prev.length >= 2) return [prev[1], inst.rank];
        return [...prev, inst.rank];
      });
    } else {
      setSelected(inst);
      setFocusRank(inst.rank);
      setRevealNonce((n) => n + 1);
      setHighlight({ rank: inst.rank, nonce: Date.now() });
      setPanelCollapsed(false);
    }
  };

  const handleCompareMode = (on) => {
    setCompareMode(on);
    if (on) setTourActive(false);
    else setCompareList([]);
  };

  const handleTour = (on) => {
    if (on) {
      setCompareMode(false);
      setCompareList([]);
      setTourIndex(0);
      setTourActive(true);
    } else {
      setTourActive(false);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Shareable link copied to clipboard");
    } catch (e) {
      toast.error("Could not copy link");
    }
  };

  const handleRegion = (key) => {
    const r = REGIONS[key];
    if (r) setRegion({ ...r, key, nonce: Date.now() });
  };

  const handleSnapshot = () => {
    const canvas = document.querySelector(".canvas-layer canvas");
    if (!canvas) {
      toast.error("Canvas not ready");
      return;
    }
    try {
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = "resiliency-rating.png";
      a.click();
      toast.success("Snapshot downloaded");
    } catch (e) {
      toast.error("Snapshot failed");
    }
  };

  const handleSealUploaded = (rank, sealImage) => {
    setInstitutions((prev) =>
      prev.map((i) => (i.rank === rank ? { ...i, seal_image: sealImage } : i))
    );
    setSelected((s) => (s && s.rank === rank ? { ...s, seal_image: sealImage } : s));
    toast.success(sealImage ? "Seal updated" : "Seal removed");
  };

  const active = hovered || selected;
  const activeRank = hovered?.rank ?? selected?.rank ?? null;
  const tourInst = tourActive ? institutions[tourIndex] : null;

  const immersiveOn = !showHeader && !showSidebar && !showFooter && panelCollapsed;
  const toggleZone = (zone) => {
    if (zone === "header") setShowHeader((v) => !v);
    else if (zone === "sidebar") setShowSidebar((v) => !v);
    else if (zone === "footer") setShowFooter((v) => !v);
  };
  const handleImmersive = () => {
    const next = !immersiveOn;
    setShowHeader(!next);
    setShowSidebar(!next);
    setShowFooter(!next);
    setPanelCollapsed(next);
  };

  return (
    <div className="resiliency-root" data-testid="resiliency-root">
      <Toaster position="top-center" richColors />
      <SealManager
        open={showSeals}
        institutions={institutions}
        onUploaded={handleSealUploaded}
        onClose={() => setShowSeals(false)}
      />
      <div className="canvas-layer">
        {institutions.length > 0 && (
          <Scene
            institutions={institutions}
            activeRank={activeRank}
            focusRank={focusRank}
            revealNonce={revealNonce}
            onHover={setHovered}
            onSelect={handleSelect}
            onClusterHover={setHoveredCluster}
            xrStore={xrStore}
            category={category}
            ranges={ranges}
            compareList={compareList}
            bloom={bloom}
            region={region}
            visibleRanks={visibleRanks}
            highlight={highlight}
          />
        )}
      </div>

      {loading && (
        <div className="loading-veil" data-testid="loading-state">
          <Loader2 className="spin" size={26} />
          <span>Calibrating spatial index…</span>
        </div>
      )}

      <div className={`hud-layer${embed ? " hud-embed" : ""}`}>
          {!embed && (
            <ViewControls
              showHeader={showHeader}
              showSidebar={showSidebar}
              showFooter={showFooter}
              onToggle={toggleZone}
              immersiveOn={immersiveOn}
              onImmersive={handleImmersive}
            />
          )}

          {embed && (
            <button
              className="embed-launch"
              data-testid="launch-full-app"
              onClick={() =>
                window.open(
                  `${window.location.origin}${window.location.pathname}`,
                  "_blank",
                  "noopener"
                )
              }
              title="Open the full interactive app in a new tab"
            >
              <Maximize2 size={14} strokeWidth={2} />
              Launch Full App
            </button>
          )}

          {showHeader && <Header />}

          {showHeader && (
            <ControlBar
              category={category}
              onCategory={setCategory}
              bloom={bloom}
              onBloom={setBloom}
              compareMode={compareMode}
              onCompareMode={handleCompareMode}
              tourActive={tourActive}
              onTour={handleTour}
              onShare={handleShare}
              onSnapshot={handleSnapshot}
              onManageSeals={() => setShowSeals(true)}
            />
          )}

          {showHeader && <HeatmapLegend category={category} ranges={ranges} />}

          {showHeader && rankFilter !== "all" && (
            <div className="filter-chip" data-testid="filter-chip">
              <span className="filter-chip-label">
                Filter · {RANK_FILTERS.find((f) => f.v === rankFilter)?.l}
              </span>
              <button
                className="filter-chip-clear"
                data-testid="filter-chip-clear"
                onClick={() => setRankFilter("all")}
                aria-label="Clear rank filter"
              >
                ×
              </button>
            </div>
          )}

          {showFooter && (
            <RegionControls
              activeRegion={region?.key}
              onRegion={handleRegion}
            />
          )}

          {hoveredCluster && (
            <div className="state-spotlight" data-testid="state-spotlight">
              <div className="state-spotlight-head">
                <span
                  className="state-spotlight-dot"
                  style={{ background: hoveredCluster.color }}
                />
                <span className="state-spotlight-name">
                  {stateName(hoveredCluster.state)}
                </span>
                <span className="state-spotlight-count">
                  {hoveredCluster.count}
                  <small>{hoveredCluster.count === 1 ? " school" : " schools"}</small>
                </span>
              </div>
              <div className="state-spotlight-list">
                {hoveredCluster.schools.slice(0, 6).map((s) => (
                  <span key={s.rank} className="state-spotlight-row">
                    <span className="ssp-rank">#{s.rank}</span>
                    {s.short}
                  </span>
                ))}
                {hoveredCluster.count > 6 && (
                  <span className="state-spotlight-more">
                    +{hoveredCluster.count - 6} more
                  </span>
                )}
              </div>
            </div>
          )}

          {showSidebar && (
            <Leaderboard
              institutions={filteredInstitutions}
              activeRank={activeRank}
              onHover={setHovered}
              onSelect={handleSelect}
              compareMode={compareMode}
              compareList={compareList}
              rankFilter={rankFilter}
              onRankFilter={setRankFilter}
              conference={conference}
              onConference={setConference}
              conferences={conferences}
              instType={instType}
              onInstType={setInstType}
              sortKey={sortKey}
              onSortKey={setSortKey}
              totalCount={institutions.length}
              collapsible={embed}
              collapsed={leaderboardCollapsed}
              onToggleCollapse={() => setLeaderboardCollapsed((c) => !c)}
            />
          )}

          <div
            className={`side-dock${panelCollapsed ? " side-dock-collapsed" : ""}`}
            data-testid="side-dock"
            style={{ display: immersiveOn ? "none" : undefined }}
          >
            <button
              className="side-handle"
              data-testid="panel-toggle"
              onClick={() => setPanelCollapsed((c) => !c)}
              aria-label={panelCollapsed ? "Show dashboard" : "Hide dashboard"}
              title={panelCollapsed ? "Show dashboard" : "Hide dashboard"}
            >
              {panelCollapsed ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </button>
            {compareMode ? (
              <ComparePanel
                a={byRank[compareList[0]]}
                b={byRank[compareList[1]]}
                onClear={(rank) =>
                  setCompareList((prev) => prev.filter((r) => r !== rank))
                }
              />
            ) : (
              <ResiliencyDashboard institution={active} />
            )}
          </div>

          {tourActive && (
            <TourCard
              institution={tourInst}
              index={tourIndex}
              total={TOUR_SIZE}
              onStop={() => setTourActive(false)}
              muted={tourMuted}
              onToggleMute={() => setTourMuted((m) => !m)}
            />
          )}

          <div className="hud-footer" style={{ display: showFooter ? undefined : "none" }}>
            <div className="hud-legend" data-testid="visual-legend">
              <LegendItem swatch="height" label="Height · Financial + Innovation" />
              <LegendItem swatch="mass" label="Mass · Real-Estate Footprint" />
              <LegendItem swatch="glow" label="Glow · Prestige + Brand" />
              <LegendItem swatch="flicker" label="Flicker · Low Adaptability" />
            </div>
            <button
              className="vr-button"
              data-testid="vr-enter-button"
              onClick={() => {
                try {
                  const p = xrStore.enterVR();
                  if (p && p.catch) p.catch(() => {});
                } catch (e) {
                  /* WebXR unsupported on this device */
                }
              }}
            >
              <Headset size={17} strokeWidth={1.8} />
              {xrSupported ? "Enter VR" : "Enter VR (WebXR)"}
            </button>
          </div>

          {embed && (
            <div className="embed-watermark" data-testid="embed-watermark">
              Resiliency Rating · The Academic Boardroom
            </div>
          )}

          {embed && (
            <button
              className="embed-vr"
              data-testid="embed-vr-button"
              onClick={() => {
                try {
                  const p = xrStore.enterVR();
                  if (p && p.catch) p.catch(() => {});
                } catch (e) {
                  /* WebXR unsupported on this device */
                }
              }}
            >
              <Headset size={16} strokeWidth={1.9} />
              {xrSupported ? "Enter VR" : "Enter VR (WebXR)"}
            </button>
          )}
        </div>
    </div>
  );
}

function LegendItem({ swatch, label }) {
  return (
    <div className="legend-item">
      <span className={`legend-swatch legend-${swatch}`} />
      <span className="legend-text">{label}</span>
    </div>
  );
}
